import { useState, useEffect, useRef } from 'react'

export type Matches = {
  small: boolean
  medium: boolean
  large: boolean
}

export function useContainerQueryFallback(breakpoints: {
  small: number
  medium: number
  large: number
}) {
  const [containerSize, setContainerSize] = useState<{
    width: number
    matches: Matches
  }>({
    width: 0,
    matches: {
      small: false,
      medium: false,
      large: false,
    },
  })
  const containerRef = useRef<HTMLDivElement>(null)
  const previousWidthRef = useRef(0)

  useEffect(() => {
    if (!containerRef.current || typeof window.ResizeObserver === 'undefined') {
      return
    }

    const container = containerRef.current

    const evaluateBreakpoints = () => {
      const containerWidth = container.clientWidth

      if (previousWidthRef.current === containerWidth) {
        return
      }

      previousWidthRef.current = containerWidth

      const breakpointMatches = {
        small: false,
        medium: false,
        large: false,
      }

      Object.entries(breakpoints).forEach(([name, width]) => {
        breakpointMatches[name as keyof typeof breakpoints] = containerWidth >= width
      })

      setContainerSize(prevSize => {
        const hasMatchesChanged = Object.entries(breakpointMatches).some(
          ([key, value]) => prevSize.matches[key as keyof typeof breakpointMatches] !== value
        )

        if (containerWidth === prevSize.width && !hasMatchesChanged) {
          return prevSize
        }

        return {
          width: containerWidth,
          matches: breakpointMatches,
        }
      })
    }

    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        evaluateBreakpoints()
      })
    })

    resizeObserver.observe(container)

    evaluateBreakpoints()

    // eslint-disable-next-line
    return () => {
      resizeObserver.unobserve(container)
      resizeObserver.disconnect()
    }
  }, [breakpoints])

  return { containerRef, ...containerSize }
}
